<script setup>
import { onBeforeUnmount } from 'vue';
import { useToast } from '@/composables/useToast';

// The app's toasts (see useToast), styled after shadcn's Sonner: a stack in
// the top-right corner, newest on top. A toast closes after its duration;
// hovering the stack pauses that so it can be read, and the × closes it.
const toast = useToast();

const ICONS = {
  success: 'mdi-check-circle',
  error: 'mdi-alert-circle',
  info: 'mdi-information',
  warning: 'mdi-alert',
};

// Remaining time per toast, so a hover pauses rather than restarts it.
const timers = new Map(); // id → { handle, remaining, startedAt }

function start(t) {
  const timer = timers.get(t.id) ?? { remaining: t.duration };
  timer.startedAt = Date.now();
  timer.handle = setTimeout(() => close(t.id), timer.remaining);
  timers.set(t.id, timer);
}
function pauseAll() {
  timers.forEach((timer) => {
    clearTimeout(timer.handle);
    timer.remaining -= Date.now() - timer.startedAt;
  });
}
function resumeAll() {
  toast.state.toasts.forEach(start);
}
function close(id) {
  clearTimeout(timers.get(id)?.handle);
  timers.delete(id);
  toast.dismiss(id);
}

// Each toast starts its timer when it appears.
function onEnter(el) {
  const t = toast.state.toasts.find((x) => String(x.id) === el.dataset.id);
  if (t) start(t);
}
function onLeave(el) {
  const id = Number(el.dataset.id);
  clearTimeout(timers.get(id)?.handle);
  timers.delete(id);
}

onBeforeUnmount(() => timers.forEach((timer) => clearTimeout(timer.handle)));
</script>

<template>
  <section class="ui-toaster" aria-label="Notifications" @mouseenter="pauseAll" @mouseleave="resumeAll">
    <TransitionGroup name="ui-toast" tag="ol" class="ui-toaster__list" @enter="onEnter" @leave="onLeave">
      <li
        v-for="t in toast.state.toasts"
        :key="t.id"
        :data-id="t.id"
        class="ui-toast"
        :class="`ui-toast--${t.type}`"
        :role="t.type === 'error' ? 'alert' : 'status'"
        :aria-live="t.type === 'error' ? 'assertive' : 'polite'"
      >
        <v-icon size="18" class="ui-toast__icon">{{ ICONS[t.type] ?? ICONS.info }}</v-icon>
        <div class="ui-toast__body">
          <p v-if="t.title" class="ui-toast__title">{{ t.title }}</p>
          <p class="ui-toast__message" :class="{ 'ui-toast__message--only': !t.title }">{{ t.message }}</p>
        </div>
        <button type="button" class="ui-toast__close" aria-label="Close" @click="close(t.id)">
          <v-icon size="14">mdi-close</v-icon>
        </button>
      </li>
    </TransitionGroup>
  </section>
</template>

<style scoped>
.ui-toaster {
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 3000;
  width: 356px;
  max-width: calc(100vw - 32px);
  pointer-events: none;
}
.ui-toaster__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.ui-toast {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 32px 14px 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--card);
  color: var(--foreground);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  pointer-events: auto;
}
.ui-toast__icon {
  flex-shrink: 0;
  margin-top: 1px;
}
.ui-toast--success .ui-toast__icon {
  color: #16a34a;
}
.ui-toast--error .ui-toast__icon {
  color: #dc2626;
}
.ui-toast--warning .ui-toast__icon {
  color: #d97706;
}
.ui-toast--info .ui-toast__icon {
  color: var(--muted-foreground);
}
.ui-toast__body {
  min-width: 0;
}
.ui-toast__title,
.ui-toast__message {
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}
.ui-toast__title {
  font-weight: 600;
}
.ui-toast__message {
  color: var(--muted-foreground);
}
.ui-toast__message--only {
  color: var(--foreground);
  font-weight: 500;
}
.ui-toast__close {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  padding: 3px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--muted-foreground);
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s ease;
}
.ui-toast:hover .ui-toast__close,
.ui-toast__close:focus-visible {
  opacity: 1;
}
.ui-toast__close:hover {
  background: var(--accent);
  color: var(--foreground);
}
.ui-toast__close:focus-visible {
  outline: 2px solid var(--brand);
}
@media (hover: none) {
  .ui-toast__close {
    opacity: 1;
  }
}

/* Slide in from the right; the rest of the stack eases into place. */
.ui-toast-enter-active,
.ui-toast-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.ui-toast-move {
  transition: transform 0.25s ease;
}
.ui-toast-enter-from,
.ui-toast-leave-to {
  opacity: 0;
  transform: translateX(24px);
}
.ui-toast-leave-active {
  position: absolute;
  width: 100%;
}
@media (prefers-reduced-motion: reduce) {
  .ui-toast-enter-active,
  .ui-toast-leave-active,
  .ui-toast-move {
    transition: opacity 0.15s ease;
  }
  .ui-toast-enter-from,
  .ui-toast-leave-to {
    transform: none;
  }
}
</style>
